import styled, { createGlobalStyle, css } from 'styled-components';

export const MODAL_CARD_ID = 'gator-protect-upgrade-cycle-modal-card';
export const MODAL_CARD_SELECTOR = `#${MODAL_CARD_ID}`;

type ModalCloseIconOverrideProps = {
  /** Same value as Modal `testId`; card element id is `${modalOverlayTestId}-card` (gator Modal). */
  modalOverlayTestId: string;
};

/** Styles the close control wrapper: first direct `div` child of the modal card (`#${testId}-card`). */
export const ModalCloseIconOverride = createGlobalStyle<ModalCloseIconOverrideProps>`
  ${({ modalOverlayTestId }) => css`
    #${modalOverlayTestId}-card > div:first-child {
      background: transparent !important;

      svg,
      svg path {
        stroke: #fff !important;
      }

      &:hover svg,
      &:hover svg path,
      &:active svg,
      &:active svg path {
        stroke: #fff !important;
      }
    }
  `}
`;

export const GradientShell = styled.div`
  ${({ theme }) => css`
    background: linear-gradient(
      180deg,
      ${theme.v3.colors.brand.snappyBlue[60]} 0%,
      #0081b6 21%,
      #00a4be 41%,
      #46c4c0 60%,
      #86e3c1 80%,
      #c6ffc8 100%
    );
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    font-family: ${theme.v3.font.family.brand};
    padding: ${theme.v3.spacing.xxxs} ${theme.v3.spacing.xxs} 112px;
    position: relative;
    width: 100%;
    border-radius: ${theme.v3.border.radius.md};
    overflow: hidden;

    @media (max-width: ${theme.v3.breakpoints.sm}) {
      min-height: 100vh;
      padding: ${theme.v3.spacing.md} ${theme.v3.spacing.xxxs} ${theme.v3.spacing.xxl};
    }
  `}
`;

export const BrandByMonarxWrapper = styled.div`
  ${({ theme }) => css`
    margin-bottom: ${theme.v3.spacing.xxxs};
  `}
`;

export const HeroBlock = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    margin-top: ${theme.v3.spacing.xxs};
    padding: 0 ${theme.v3.spacing.xxxs};
    text-align: center;
  `}
`;

export const HeroTitle = styled.h2`
  ${({ theme }) => css`
    color: ${theme.v3.colors.neutral[100]};
    font-size: ${theme.v3.font.sizes['2xlarge']};
    font-weight: ${theme.v3.font.weights.medium};
    line-height: ${theme.v3.font.lineHeight.relaxed};
    margin: 0;

    @media (max-width: ${theme.v3.breakpoints.sm}) {
      font-size: ${theme.v3.font.sizes['1xlarge']};
    }
  `}
`;

export const HeroSubtitle = styled.p`
  ${({ theme }) => css`
    color: ${theme.v3.colors.neutral[100]};
    font-size: ${theme.v3.font.sizes.medium};
    font-weight: ${theme.v3.font.weights.regular};
    line-height: ${theme.v3.font.lineHeight.comfortable};
    margin: 0;
    width: 100%;
    margin-bottom: ${theme.v3.spacing.xxs};

    strong {
      font-weight: ${theme.v3.font.weights.medium};
    }
  `}
`;

export const InnerCard = styled.div`
  ${({ theme }) => css`
    align-self: center;
    background: ${theme.v3.colors.neutral[100]};
    border-radius: 20px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    max-width: 528px;
    padding: ${theme.v3.spacing.xxs};
    width: 100%;
    z-index: 1;
  `}
`;

export const FeatureList = styled.ul`
  ${({ theme }) => css`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.v3.spacing.xxxs};
  list-style: none;
  margin: 0;
  margin-bottom: ${theme.v3.spacing.xs};
  padding: 0;
    width: 100%;
  `}
`;

export const FeatureRow = styled.li`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.v3.spacing.nano};
`;

export const FeatureText = styled.span`
  ${({ theme }) => css`
    color: ${theme.v3.colors.neutral[20]};
    font-size: ${theme.v3.font.sizes.medium};
    font-weight: ${theme.v3.font.weights.regular};
    line-height: ${theme.v3.font.lineHeight.comfortable};
    text-align: left;
  `}
`;

export const PlanBlock = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.v3.spacing.nano};
    width: 100%;
    margin-bottom: ${theme.v3.spacing.xxxs};
  `}
`;

export const SelectFieldWrap = styled.div`
  width: 100%;
`;

export const UpgradeCycleOptionLabel = styled.span`
  ${() => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  `}
`;

export const PlanLabel = styled.span`
  ${({ theme }) => css`
    color: ${theme.v3.colors.neutral[40]};
    font-size: ${theme.v3.font.sizes.medium};
    font-weight: ${theme.v3.font.weights.medium};
    line-height: ${theme.v3.font.lineHeight.relaxed};
    width: 100%;
    text-align: left;
  `}
`;

export const DiscountBadge = styled.span`
  ${({ theme }) => css`
    background: ${theme.v3.colors.contextual.active[98]};
    border-radius: ${theme.v3.border.radius.sm};
    color: ${theme.v3.colors.contextual.active[30]};
    font-size: ${theme.v3.font.sizes.medium};
    font-weight: ${theme.v3.font.weights.medium};
    line-height: ${theme.v3.font.lineHeight.comfortable};
    padding: 2px ${theme.v3.spacing.nano};
    position: absolute;
    right: ${theme.v3.spacing.md};
    top: 38px;
    white-space: nowrap;

    @media (max-width: ${theme.v3.breakpoints.sm}) {
      right: ${theme.v3.spacing.xxxs};
    }
  `}
`;

export const TermsStrip = styled.div`
  ${({ theme }) => css`
    background: ${theme.v3.colors.neutral[98]};
    border-radius: ${theme.v3.border.radius.md};
    color: ${theme.v3.colors.neutral[20]};
    font-size: ${theme.v3.font.sizes.mini};
    line-height: ${theme.v3.font.lineHeight.comfortable};
    padding: ${theme.v3.spacing.nano} ${theme.v3.spacing.xxxs};
    width: 100%;
    text-align: left;
    margin-bottom: ${theme.v3.spacing.xxxs};

    a {
      color: ${theme.v3.colors.neutral[20]};
      font-size: ${theme.v3.font.sizes.mini};
      text-decoration: underline;
    }
  `}
`;

export const TermsLine = styled.p`
  margin: 0;
`;

export const ModalProtectionCtaWrap = styled.div`
  ${({ theme }) => css`
  width: 100%;
  margin-bottom: ${theme.v3.spacing.xs};

  & > div {
    display: flex;
    width: 100%;
  }

  & button {
    width: 100%;
  }
`}
`;

export const RefundRow = styled.div`
  ${({ theme }) => css`
    align-items: center;
    display: flex;
    gap: ${theme.v3.spacing.quarck};
    justify-content: center;
    margin-top: -${theme.v3.spacing.nano};
    width: 100%;
  `}
`;

export const RefundCopy = styled.span`
  ${({ theme }) => css`
    color: ${theme.v3.colors.neutral[60]};
    font-size: ${theme.v3.font.sizes.mini};
    font-weight: ${theme.v3.font.weights.medium};
    line-height: ${theme.v3.font.lineHeight.compact};
  `}
`;

export const MascotWrap = styled.div`
  ${() => css`
    bottom: -70px;
    height: auto;
    pointer-events: none;
    position: absolute;
    right: -34px;
    width: auto;

    img {
      display: block;
      height: auto;
      max-height: 100%;
      transform: scaleX(-1);
      width: 100%;
      
    }
  `}
`;
